/**
 * Visionary Editor 主题配置系统
 * 提供动态主题切换和颜色配置功能
 */

export interface ThemeConfig {
    name: string;
    colors: {
        primary: string;
        primaryHover: string;
        primaryLight: string;
        bgColor: string;
        bgSecondary: string;
        bgTertiary: string;
        textColor: string;
        textSecondary: string;
        textMuted: string;
        textInverse: string;
        borderColor: string;
        borderLight: string;
        borderDark: string;
        success: string;
        successHover: string;
        danger: string;
        dangerHover: string;
        warning: string;
        info: string;
    };
    shadows: {
        sm: string;
        md: string;
        lg: string;
    };
    borderRadius: {
        sm: string;
        md: string;
        lg: string;
    };
    spacing: {
        xs: string;
        sm: string;
        md: string;
        lg: string;
        xl: string;
    };
}

// 默认主题
export const defaultTheme: ThemeConfig = {
    name: 'default',
    colors: {
        primary: '#007bff',
        primaryHover: '#0056b3',
        primaryLight: '#e7f3ff',
        bgColor: '#ffffff',
        bgSecondary: '#f8f9fa',
        bgTertiary: '#e9ecef',
        textColor: '#212529',
        textSecondary: '#6c757d',
        textMuted: '#adb5bd',
        textInverse: '#ffffff',
        borderColor: '#dee2e6',
        borderLight: '#e9ecef',
        borderDark: '#adb5bd',
        success: '#28a745',
        successHover: '#218838',
        danger: '#dc3545',
        dangerHover: '#c82333',
        warning: '#ffc107',
        info: '#17a2b8',
    },
    shadows: {
        sm: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
        md: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
        lg: '0 1rem 3rem rgba(0, 0, 0, 0.175)',
    },
    borderRadius: {
        sm: '0.25rem',
        md: '0.375rem',
        lg: '0.5rem',
    },
    spacing: {
        xs: '0.25rem',
        sm: '0.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '3rem',
    },
};

// 暗色主题
export const darkTheme: ThemeConfig = {
    name: 'dark',
    colors: {
        primary: '#0d6efd',
        primaryHover: '#0b5ed7',
        primaryLight: '#031633',
        bgColor: '#020202',
        bgSecondary: '#343a40',
        bgTertiary: '#495057',
        textColor: '#ffffff',
        textSecondary: '#adb5bd',
        textMuted: '#6c757d',
        textInverse: '#ffffff',
        borderColor: '#495057',
        borderLight: '#6c757d',
        borderDark: '#343a40',
        success: '#198754',
        successHover: '#157347',
        danger: '#dc3545',
        dangerHover: '#c82333',
        warning: '#ffc107',
        info: '#0dcaf0',
    },
    shadows: {
        sm: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.3)',
        md: '0 0.5rem 1rem rgba(0, 0, 0, 0.4)',
        lg: '0 1rem 3rem rgba(0, 0, 0, 0.5)',
    },
    borderRadius: {
        sm: '0.25rem',
        md: '0.375rem',
        lg: '0.5rem',
    },
    spacing: {
        xs: '0.25rem',
        sm: '0.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '3rem',
    },
};

// 蓝色主题
export const blueTheme: ThemeConfig = {
    name: 'blue',
    colors: {
        primary: '#0d6efd',
        primaryHover: '#0b5ed7',
        primaryLight: '#cfe2ff',
        bgColor: '#ffffff',
        bgSecondary: '#f8f9ff',
        bgTertiary: '#e7f1ff',
        textColor: '#1a1a1a',
        textSecondary: '#4a5568',
        textMuted: '#718096',
        textInverse: '#ffffff',
        borderColor: '#cbd5e0',
        borderLight: '#e2e8f0',
        borderDark: '#a0aec0',
        success: '#38a169',
        successHover: '#2f855a',
        danger: '#e53e3e',
        dangerHover: '#c53030',
        warning: '#d69e2e',
        info: '#3182ce',
    },
    shadows: {
        sm: '0 0.125rem 0.25rem rgba(13, 110, 253, 0.1)',
        md: '0 0.5rem 1rem rgba(13, 110, 253, 0.15)',
        lg: '0 1rem 3rem rgba(13, 110, 253, 0.2)',
    },
    borderRadius: {
        sm: '0.25rem',
        md: '0.375rem',
        lg: '0.5rem',
    },
    spacing: {
        xs: '0.25rem',
        sm: '0.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '3rem',
    },
};
// 蓝色主题
export const visionTheme: ThemeConfig = {
    name: 'custom',
    colors: {
        primary: '#0d6efd',
        primaryHover: '#3B3B3B',
        primaryLight: '#262626',
        bgColor: '#000000',
        bgSecondary: '#f8f9ff',
        bgTertiary: '#e7f1ff',
        textColor: '#1a1a1a',
        textSecondary: '#4a5568',
        textMuted: '#718096',
        textInverse: '#ffffff',
        borderColor: '#cbd5e0',
        borderLight: '#e2e8f0',
        borderDark: '#a0aec0',
        success: '#38a169',
        successHover: '#2f855a',
        danger: '#e53e3e',
        dangerHover: '#c53030',
        warning: '#d69e2e',
        info: '#3182ce',
    },
    shadows: {
        sm: '0 0.125rem 0.25rem rgba(13, 110, 253, 0.1)',
        md: '0 0.5rem 1rem rgba(13, 110, 253, 0.15)',
        lg: '0 1rem 3rem rgba(13, 110, 253, 0.2)',
    },
    borderRadius: {
        sm: '0.25rem',
        md: '0.375rem',
        lg: '0.5rem',
    },
    spacing: {
        xs: '0.25rem',
        sm: '0.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '3rem',
    },
};

export class ThemeManager {
    private currentTheme: ThemeConfig;
    private themes: Map<string, ThemeConfig> = new Map();

    constructor() {
        this.currentTheme = visionTheme;
        this.registerTheme(visionTheme);
        this.registerTheme(defaultTheme);
        this.registerTheme(darkTheme);
        this.registerTheme(blueTheme);
    }

    /**
     * 注册新主题
     */
    registerTheme(theme: ThemeConfig): void {
        this.themes.set(theme.name, theme);
    }

    /**
     * 获取所有可用主题
     */
    getAvailableThemes(): ThemeConfig[] {
        return Array.from(this.themes.values());
    }

    /**
     * 获取当前主题
     */
    getCurrentTheme(): ThemeConfig {
        return this.currentTheme;
    }

    /**
     * 切换主题
     */
    setTheme(themeName: string): boolean {
        const theme = this.themes.get(themeName);
        if (!theme) {
            console.warn(`Theme "${themeName}" not found`);
            return false;
        }

        this.currentTheme = theme;
        this.applyTheme(theme);
        return true;
    }

    /**
     * 应用主题到DOM
     */
    private applyTheme(theme: ThemeConfig): void {
        const root = document.documentElement;
        
        // 应用颜色变量
        Object.entries(theme.colors).forEach(([key, value]) => {
            const cssVarName = `--vision-${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
            root.style.setProperty(cssVarName, value);
        });

        // 应用阴影变量
        Object.entries(theme.shadows).forEach(([key, value]) => {
            const cssVarName = `--vision-shadow${key === 'sm' ? '' : '-' + key}`;
            root.style.setProperty(cssVarName, value);
        });

        // 应用圆角变量
        Object.entries(theme.borderRadius).forEach(([key, value]) => {
            const cssVarName = `--vision-border-radius${key === 'sm' ? '' : '-' + key}`;
            root.style.setProperty(cssVarName, value);
        });

        // 应用间距变量
        Object.entries(theme.spacing).forEach(([key, value]) => {
            const cssVarName = `--vision-spacing-${key}`;
            root.style.setProperty(cssVarName, value);
        });

        // 触发主题变化事件
        window.dispatchEvent(new CustomEvent('themeChanged', { 
            detail: { theme: theme } 
        }));
    }

    /**
     * 创建自定义主题
     */
    createCustomTheme(name: string, baseTheme: ThemeConfig, overrides: Partial<ThemeConfig>): ThemeConfig {
        const customTheme: ThemeConfig = {
            name,
            colors: { ...baseTheme.colors, ...overrides.colors },
            shadows: { ...baseTheme.shadows, ...overrides.shadows },
            borderRadius: { ...baseTheme.borderRadius, ...overrides.borderRadius },
            spacing: { ...baseTheme.spacing, ...overrides.spacing },
        };
        
        this.registerTheme(customTheme);
        return customTheme;
    }
}

// 创建全局主题管理器实例
export const themeManager = new ThemeManager();
